import { Injectable, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, map, Observable, take } from 'rxjs';
import { ReleaseNotesService } from './release-notes.service';
import { UserSettingsService } from './user-settings.service';
import { UserSettingStore } from './user-setting-store.base';
import { OverlayTeardownService } from './overlay-teardown.service';
import { ReleaseNotesDialogComponent } from 'src/app/features/release-notes/components/release-notes-dialog/release-notes-dialog.component';

const NOTIFY_KEY = 'releaseNotes.notifyOnLogin';
const LAST_SEEN_KEY = 'releaseNotes.lastSeenVersion';

/** Cross-surface state for release notes: tracks the per-user "seen" marker (server-side),
 *  drives the header unread dot, and pops the login modal once per session when appropriate. */
@Injectable({ providedIn: 'root' })
export class ReleaseNotesStateService extends UserSettingStore {
  private latest: string | null = null;
  private ready$ = new BehaviorSubject<boolean>(false);
  private lastSeen$ = new BehaviorSubject<string | null>(null);

  hasUnseen$: Observable<boolean> = combineLatest([this.ready$, this.lastSeen$]).pipe(
    map(([ready, lastSeen]) => ready && this.latest != null && this.latest !== lastSeen)
  );

  constructor(
    private releaseNotes: ReleaseNotesService,
    userSettings: UserSettingsService,
    private dialog: MatDialog,
    store: Store,
    zone: NgZone,
    private overlayTeardown: OverlayTeardownService
  ) {
    super(userSettings, store, zone);
  }

  /** Wire the post-login prompt. Fires once per authenticated identity on an /app route — an in-app
   *  logout/login re-evaluates instead of going silent until an F5, while the "unseen" gate still
   *  ensures the dialog only actually opens once per release. Safe to call multiple times. */
  initLoginPrompt(): void {
    this.init();
  }

  /** The base's per-login load hook. Loads the seen marker + latest version and, when
   *  appropriate, pops the login modal. */
  protected load(userId: number): void {
    this.loadThenMaybePrompt(userId);
  }

  /** The release-notes "seen" marker isn't cross-tab synced (it's written host-locally on
   *  dialog close via userSettings.set, never through the base's persist()). This store neither
   *  posts nor consumes on the shared channel, so opt out of it entirely — otherwise the base
   *  would open a BroadcastChannel and re-enter the Angular zone (scheduling a full CD pass) on
   *  every date/number preset change in another tab, just to run the no-op onBroadcast below. */
  protected override get syncsAcrossTabs(): boolean {
    return false;
  }

  protected onBroadcast(): void {
    // no-op (see syncsAcrossTabs)
  }

  private loadThenMaybePrompt(userId: number): void {
    // Use the STRICT shared load (keyed by userId) so a final failure — after its internal retries —
    // reaches the error handler instead of arriving as an indistinguishable `{}`. A swallowed `{}` reads
    // lastSeen as null and — if there IS a latest release — re-opens the release-notes dialog for a
    // release the user already dismissed, then never retries (the base committed the per-identity guard
    // before the load). getAllSharedStrict shares the EXACT same single request as DateFormatService's
    // load, so pairing the two once-per-login consumers still fires only ONE GET /v2/userSettings.
    combineLatest([this.releaseNotes.latestVersion(), this.userSettings.getAllSharedStrict(userId)])
      .pipe(take(1))
      .subscribe({
        next: ([latest, settings]) => {
          this.latest = latest;
          this.lastSeen$.next((settings[LAST_SEEN_KEY] as string) ?? null);
          this.ready$.next(true);

          const notifyDisabled = settings[NOTIFY_KEY] === false;
          const unseen = latest != null && latest !== this.lastSeen$.value;
          if (!notifyDisabled && unseen) {
            this.openDialog();
          }
        },
        error: () => {
          // Load failed (after the internal retries). Do NOT prompt on incomplete data — never open the
          // dialog off a phantom "unseen". Re-arm the guard (rearmLoad) so a later app-ready transition
          // retries, but only while it still points at THIS load (a newer login may have advanced it).
          this.rearmLoad(userId);
        },
      });
  }

  private openDialog(): void {
    const ref = this.dialog.open(ReleaseNotesDialogComponent, { width: '560px', maxWidth: '95vw' });
    // afterUserClosed, NOT afterClosed: session teardown (and a cross-tab identity switch) force-closes
    // every open dialog via OverlayTeardownService, and a forced close emits `undefined` exactly like a
    // user dismissal. Marking seen there burns the once-per-release prompt for notes the user never
    // read, and fires the PUT on a session that is already gone (401 → another teardown) or against the
    // WRONG user's settings. Only a real dismissal counts as "seen".
    this.overlayTeardown
      .afterUserClosed(ref)
      .pipe(take(1))
      .subscribe(() => this.markSeen());
  }

  /** Record the current latest version as seen for this user (server-side) and clear the dot. */
  markSeen(): void {
    if (this.latest == null || this.latest === this.lastSeen$.value) {
      return;
    }
    const version = this.latest;
    this.lastSeen$.next(version);
    this.userSettings.set(LAST_SEEN_KEY, version).pipe(take(1)).subscribe({ error: () => {} });
  }
}
